import React, { useEffect, useLayoutEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './store/Auth';
import { LanguageProvider, T } from './components/Text';
import Api from './services/api';
import Layout from './components/Layout';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Home from './pages/Home';
import Orders from './pages/Orders';
import Upload from './pages/Upload';
import Customers from './pages/Customers';
import Account from './pages/Account';
import './assets/theme/index.css';

const BusinessCard = lazy(() => import('./pages/BusinessCard'));

const useThemeEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

function Authorization({ permission }) {
  const { user, loading, error } = useAuth();
  if (loading) return <p className="react-loading" role="status"><T>Yükleniyor…</T></p>;
  if (error && !user) return (
    <p className="form-error" role="alert">
      <T>{error}</T>
      <button type="button" onClick={() => window.location.reload()}><T>Yenile</T></button>
    </p>
  );
  if (!user) return <Navigate to={'/login?next=' + encodeURIComponent(typeof window === 'undefined' ? '/orders' : window.location.pathname)} replace />;
  if (permission === 'manager' && !Api.isManager(user)) return <Navigate to={Api.home(user)} replace />;
  if (permission === 'customer' && Api.isManager(user)) return <Navigate to={Api.home(user)} replace />;
  if (permission === 'manage' && !Api.canManageBranch(user)) return <Navigate to="/orders" replace />;
  return <Outlet />;
}

function AppContent() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const publicPage = ['/', '/index.html', '/login.html', '/login'].includes(pathname);
  const theme = publicPage ? 'dark' : (user?.theme || 'dark');

  useThemeEffect(() => {
    document.documentElement.dataset.theme = theme;
    const colors = {
      light: '#f5f3ec',
      dark: '#141513',
      midnight: '#0D1117',
      forest: '#040805',
      plum: '#38232A',
      ice: '#EDF4FA',
      sage: '#EFF5EE',
      powder: '#FAF0F2'
    };
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', colors[theme] || colors.dark);
    document.dispatchEvent(new Event('theme-changed'));
  }, [theme]);

  return (
    <Routes>
      {/* Genel Sayfalar */}
      <Route path="/" element={<Landing />} />
      <Route path="/index.html" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/login.html" element={<Navigate to="/login" replace />} />

      {/* Yetkili Panel Sayfaları */}
      <Route element={<Authorization />}>
        <Route element={<Layout />}>
          <Route element={<Authorization permission="manager" />}>
            <Route path="/admin" element={<Home />} />
            <Route path="/admin.html" element={<Navigate to="/admin" replace />} />
            <Route path="/settings" element={<Account settingsOnly />} />
            <Route path="/settings.html" element={<Navigate to="/settings" replace />} />
          </Route>
          <Route element={<Authorization permission="customer" />}>
            <Route path="/customer" element={<Home />} />
            <Route path="/customer.html" element={<Navigate to="/customer" replace />} />
          </Route>
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders.html" element={<Navigate to="/orders" replace />} />
          <Route path="/products" element={<Orders />} />
          <Route path="/products/:id" element={<Orders />} />
          <Route path="/account" element={<Account />} />
          <Route path="/account.html" element={<Navigate to="/account" replace />} />
          <Route path="/profile" element={<Account />} />
          <Route element={<Authorization permission="manage" />}>
            <Route path="/order-create" element={<Upload />} />
            <Route path="/order-create.html" element={<Navigate to="/order-create" replace />} />
            <Route path="/orders/create" element={<Upload />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/customers.html" element={<Navigate to="/customers" replace />} />
          </Route>
        </Route>
      </Route>

      <Route path="/tag/:tag" element={<Suspense fallback={null}><BusinessCard /></Suspense>} />
      <Route path="/metrics" element={<Navigate to="/admin#metrics" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <AppContent />
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
